import './style.css';

import type { DialogActionsProps, DialogContentProps, DialogTitleProps } from '@material-ui/core';
import { Dialog, DialogActions, DialogContent, MuiThemeProvider } from '@material-ui/core';
import { Text, View } from '@zinc/ui';
import { bind } from 'bind-decorator';
import { Button, FlatButton } from 'bz-core-controls';
import refs from 'bz-utilities/modules/legacy/refs';
import React from 'react';
import ErrorPanel from './controls/errors';
import theme from './theme';

const ConfirmationTypes = {
  yesno: 'yesno',
  alert: 'alert',
  saveInfo: 'saveInfo'
};

export interface IConfirmationProps {
  title: string;
  description: string | React.ReactNode;
  confirmLabel?: string;
  abortLabel?: string;
  defaultFocus?: string;
  continueText?: string;
  warningsText?: string;
  titleProps?: Partial<DialogTitleProps>;
  contentProps?: Partial<DialogContentProps>;
  actionsProps?: Partial<DialogActionsProps>;
  type?: 'yesno' | 'alert' | 'saveInfo' | 'spinner' | 'custom';
  alert?: boolean;
  yesno?: boolean;
  promise?: Promise<any>;
  errors?: string[];
  className?: string;
  saveInfo?: boolean;
  zIndex?: number;
}

interface IState {
  open: boolean;
}

@refs('yes', 'confirm', 'dialog')
class Confirmation extends React.Component<IConfirmationProps, IState> {
  static defaultProps: Partial<IConfirmationProps> = {
    defaultFocus: 'confirm',
    title: 'Confirmation',
    description: 'Are you sure you want to do this?',
    confirmLabel: 'Yes',
    abortLabel: 'No',
    warningsText: 'Warnings',
    titleProps: {},
    contentProps: {},
    actionsProps: {},
    yesno: false,
    alert: false,
    saveInfo: false,
    zIndex: 2600
  };

  promise: Promise<boolean>;
  resolve: (value: boolean) => void = () => null;
  reject: (reason: any) => void = () => null;

  constructor(props: IConfirmationProps) {
    super(props);

    if (props.promise) {
      this.promise = new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
      props.promise
        .then((r) => {
          this.setState({ open: false });
          this.resolve(r);
        })
        .catch((err) => {
          this.setState({ open: false });
          this.reject(err);
        });
    } else {
      this.promise = new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    }

    this.state = {
      open: true
    };
  }

  @bind
  abort(e: React.MouseEvent<any>): void {
    e.preventDefault();
    return this.setState({ open: false }, () => this.reject());
  }

  @bind
  confirmTrue(e: React.MouseEvent<any>): void {
    e.preventDefault();
    return this.confirm(e, true);
  }

  confirm = (e: React.MouseEvent<any>, arg: any): void => {
    e.preventDefault();
    return this.setState({ open: false }, () => {
      this.resolve(arg);
    });
  };

  get hasErrors(): boolean {
    const { errors } = this.props;
    return !!errors && errors.length > 0;
  }

  getType = () => {
    const { type } = this.props;
    if (!type) {
      return this.getTypeFromBoolean();
    }

    return type;
  };

  getTypeFromBoolean = () => {
    const { yesno, alert, saveInfo } = this.props;

    const types = { yesno, alert, saveInfo };
    const type = Object.keys(types).find((key) => types[key]);
    return type;
  };

  renderYesNo = () => {
    return [
      <FlatButton
        key="cancel"
        label="Cancel"
        secondary={false}
        style={{ marginRight: 10 }}
        onClick={this.abort}
      />,
      <FlatButton
        key="no"
        label="No"
        secondary={true}
        style={{ marginRight: 10 }}
        onClick={this.confirm}
      />,
      <Button
        key="yes"
        ref={this._refs.yes}
        label="Yes"
        primary={true}
        onClick={this.confirmTrue}
      />
    ];
  };

  renderAlert = () => {
    const { confirmLabel } = this.props;
    return (
      <Button
        ref={this._refs.confirm}
        style={{ marginRight: 10 }}
        label={confirmLabel === 'Yes' ? 'OK' : confirmLabel}
        primary={true}
        onClick={this.confirm}
      />
    );
  };

  renderInfo = () => {
    return [
      <FlatButton
        key="no"
        label="No"
        secondary={true}
        style={{ marginRight: 10 }}
        onClick={this.confirm}
      />,
      <Button
        key="yes"
        ref={this._refs.yes}
        label="Yes"
        primary={true}
        onClick={this.confirmTrue}
      />
    ];
  };

  @bind
  renderDefaultActions() {
    const { confirmLabel, abortLabel } = this.props;
    return [
      <FlatButton
        key="abort"
        label={abortLabel}
        secondary={false}
        style={{ marginRight: 10 }}
        onClick={this.abort}
      />,
      <Button
        key="confirm"
        ref={this._refs.confirm}
        label={confirmLabel}
        primary={true}
        onClick={this.confirm}
      />
    ];
  }

  renderActions = () => {
    if (this.hasErrors) {
      return null;
    }

    const type = this.getType();
    switch (type) {
      case 'yesno':
        return this.renderYesNo();
      case 'alert':
        return this.renderAlert();
      case 'spinner':
      case 'custom':
        return null;
      default:
        return this.renderDefaultActions();
    }
  };

  render() {
    const { className } = this;

    const {
      abortLabel,
      warningsText,
      title,
      description,
      errors,
      zIndex,
      className: propsClassname,
      confirmLabel,
      yesno,
      alert,
      saveInfo,
      titleProps,
      contentProps,
      actionsProps,
      type,
      ...other
    } = this.props;

    let errorPanel;
    if (this.hasErrors) {
      errorPanel = (
        <ErrorPanel
          className={className}
          abortLabel={abortLabel}
          confirmLabel={confirmLabel}
          errors={errors!}
          warningsText={warningsText}
          abort={this.abort}
          confirm={this.confirm}
        />
      );
    }

    return (
      <div>
        <MuiThemeProvider theme={theme}>
          <Dialog
            ref={this._refs.dialog}
            openByProps={true}
            style={{ zIndex: zIndex, wordBreak: 'break-word' }}
            bodyStyle={{ padding: 10, overflow: 'auto' }}
            titleStyle={{ padding: 10, paddingTop: 20, overflow: 'auto' }}
            open={this.state.open}
            className={`${className} dialog`}
            {...other}
          >
            <View padding={6} paddingBottom={0}>
              <Text variant="featured-2" weight={'medium'}>
                {title}
              </Text>
            </View>
            <DialogContent {...contentProps}>
              {description}
              {errorPanel}
            </DialogContent>
            {type !== 'custom' && (
              <DialogActions {...actionsProps}>{this.renderActions()}</DialogActions>
            )}
          </Dialog>
        </MuiThemeProvider>
      </div>
    );
  }

  private get className(): string {
    const { className } = this.props;
    const classNames = ['bz-controls confirmation-dialog'];

    if (!!className) {
      classNames.push(className);
    }

    return classNames.join(' ');
  }
}

export default Confirmation;
